import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import feedbackDissatisfied from "@ingka/ssr-icon/paths/feedback-dissatisfied";
import feedbackHappy from "@ingka/ssr-icon/paths/feedback-happy";
import feedbackNeutral from "@ingka/ssr-icon/paths/feedback-neutral";
import feedbackSad from "@ingka/ssr-icon/paths/feedback-sad";
import feedbackSatisfied from "@ingka/ssr-icon/paths/feedback-satisfied";
import { IconPill } from "@ingka/pill";
import Tooltip from "@ingka/tooltip";

const FeedbackWrapper = styled.div`
	display: flex;
	justify-content: center;
	gap: 0.5rem;
`;

const ratingOptions = [
	{ title: "Sad", icon: feedbackSad },
	{ title: "Dissatisfied", icon: feedbackDissatisfied },
	{ title: "Neutral", icon: feedbackNeutral },
	{ title: "Satisfied", icon: feedbackSatisfied },
	{ title: "Happy", icon: feedbackHappy },
];

type RatingsProps = {
	onRatings: (score: number) => void;
	rating: number | undefined;
};

const Ratings: FC<RatingsProps> = ({ onRatings, rating }) => {
	const count = ratingOptions.length;

	const ratingElementRefs = useMemo(
		() => Array.from({ length: count }, () => React.createRef<HTMLButtonElement>()),
		[count]
	);

	const [focusIndex, setFocusIndex] = useState<number>(0);

	useEffect(() => {
		if (rating != null) {
			setFocusIndex(Math.min(Math.max(rating - 1, 0), count - 1));
		}
	}, [rating, count]);

	const focusAt = useCallback(
		(next: number) => {
			const idx = ((next % count) + count) % count;
			setFocusIndex(idx);
			const el = ratingElementRefs[idx]?.current;
			if (el && typeof el.focus === "function") {
				el.focus();
			}
		},
		[count, ratingElementRefs]
	);

	const moveFocus = useCallback(
		(fromIndex: number, delta: number) => {
			focusAt(fromIndex + delta);
		},
		[focusAt]
	);

	const selectRating = useCallback(
		(index: number) => {
			onRatings(index + 1);
		},
		[onRatings]
	);

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
			switch (e.key) {
				case "ArrowRight":
				case "ArrowDown":
					e.preventDefault();
					moveFocus(index, +1);
					break;
				case "ArrowLeft":
				case "ArrowUp":
					e.preventDefault();
					moveFocus(index, -1);
					break;
				case "Spacebar":
				case "Enter":
					e.preventDefault();
					selectRating(index);
					break;
				default:
					break;
			}
		},
		[moveFocus, selectRating]
	);

	return (
		<>
			<FeedbackWrapper role="radiogroup" aria-label="Feedback rating">
				{ratingOptions.map((option, index) => {
					const isChecked = rating === index + 1;
					const isTabbable = rating ? rating === index + 1 : index === 0;

					return (
						<Tooltip key={option.title} interactiveCustomTrigger tooltipText={option.title}>
							<IconPill
								key={option["title"]}
								role="radio"
								ref={ratingElementRefs[index]}
								tabIndex={isTabbable ? 0 : -1}
								aria-checked={isChecked}
								aria-label={option.title}
								aria-pressed={undefined}
								size="small"
								ssrIcon={option.icon}
								onClick={() => selectRating(index)}
								onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => onKeyDown(e, index)}
							/>
						</Tooltip>
					);
				})}
			</FeedbackWrapper>
		</>
	);
};

export default Ratings;
